import { AppointmentType } from "@prisma/client";

// Consults (Marble consult + group consult) are not billed therapy, so they
// do not create the same-day claim-denial risk the client-already-booked
// guard exists to prevent. Overlapping times still conflict — a client
// cannot sit in two appointments at once.

export const NOT_BILLABLE_APPOINTMENT_TYPES: AppointmentType[] = [
  AppointmentType.GROUP_CONSULT,
  AppointmentType.CONSULT,
];

/**
 * Internal-facing display names for appointment types — what staff see in the
 * CRM, task notes and Slack alerts.
 *
 * In `@marble/core` because `workers/lambdas` names appointment types in the
 * task notes it writes for Care Ops, and `apps/web` names the same types all
 * over the CRM; a second copy would drift the moment a type is renamed.
 * Client-facing wording is separate and lives in the app
 * (`AppointmentTypeEnumToStrClientFacing`).
 */
export const AppointmentTypeEnumToStr: Record<AppointmentType, string> = {
  [AppointmentType.CONSULT]: "Consult",
  [AppointmentType.INTAKE]: "Intake",
  [AppointmentType.INDIVIDUAL_THERAPY]: "Individual Therapy",
  [AppointmentType.FAMILY_THERAPY]: "Family Therapy",
  [AppointmentType.GUARDIAN_CONSULT]: "Guardian Consult",
  [AppointmentType.GROUP_THERAPY]: "Group Therapy",
  [AppointmentType.GROUP_CONSULT]: "Group Leader Meet & Greet",
  [AppointmentType.MEDICATION_MANAGEMENT]: "Medication Management",
  [AppointmentType.MEDICATION_MANAGEMENT_INTAKE]:
    "Medication Management Intake",
};

/**
 * Types that do not count as a client's first non-intake (in-care) session.
 * Consults and intake remain Care Ops work, so inbound routing stays on Care
 * Ops until a completed appointment of any other type exists.
 */
export const FIRST_NON_INTAKE_EXCLUDED_APPOINTMENT_TYPES: AppointmentType[] = [
  AppointmentType.INTAKE,
  AppointmentType.MEDICATION_MANAGEMENT_INTAKE,
  AppointmentType.CONSULT,
  AppointmentType.GUARDIAN_CONSULT,
  AppointmentType.GROUP_CONSULT,
];

/**
 * Cancellation-reason strings written by the app's auto-cancel and Headway
 * setup flows. In `@marble/core` because both the app's appointment-status
 * badge and the voice-confirmation-call ETL report classify cancellations by
 * matching against these exact strings; a second copy would drift the
 * moment either flow's wording changes.
 */
export const AUTO_CANCELLED_CANCELLATION_REASON = "Auto Cancelled";
export const AUTO_CANCELLED_CANCELLATION_REASON_INCOMPLETE_ONBOARDING =
  "Auto Cancelled - Incomplete Onboarding (cannot be rescheduled until complete)";
export const CANCELLATION_REASON_HEADWAY_SETUP_INCOMPLETE =
  "Headway Setup Incomplete (cannot be rescheduled until complete)";

/** Cancellation reasons for insurance refer-outs, written by the app and the refer-out Lambdas. */
export const CANCELLATION_REASON_INSURANCE_INELIGIBLE =
  "Insurance Ineligible (cannot be rescheduled until insurance is updated and verified)";
export const CANCELLATION_REASON_INACTIVE_INSURANCE =
  "Insurance Inactive (cannot be rescheduled until insurance is updated and verified)";

export const UNMARK_PAID_NO_SHOW_ERROR =
  "This no-show can no longer be unmarked because payment has already been issued";
